import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import { environment } from '../../../environments/environment';

/**
 * Reusable Location Input Component
 * Provides UI for user location detection and manual address input
 */
const LocationInput = ({
  userLocation,
  isGettingLocation,
  locationError,
  onGetLocation,
  onSetLocationFromAddress,
  showManualInput = false,
  onToggleManualInput,
  placeholder = 'Enter your city or address...',
  showLocationIcon = true,
  compact = false,
}) => {
  const [manualAddress, setManualAddress] = useState('');
  const [showManualInputLocal, setShowManualInputLocal] =
    useState(showManualInput);

  const handleManualLocation = async () => {
    if (!manualAddress.trim()) return;

    const result = await onSetLocationFromAddress(manualAddress);
    if (result) {
      setManualAddress('');
      setShowManualInputLocal(false);
      if (onToggleManualInput) {
        onToggleManualInput(false);
      }
    }
  };

  const handleToggleManualInput = () => {
    const newState = !showManualInputLocal;
    setShowManualInputLocal(newState);
    if (onToggleManualInput) {
      onToggleManualInput(newState);
    }
  };

  const getLocationIcon = () => {
    if (isGettingLocation) {
      return (
        <GpsNotFixedIcon
          sx={{
            color: '#FFA726',
            fontSize: 22,
            cursor: 'pointer',
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.5 },
              '100%': { opacity: 1 },
            },
          }}
          onClick={onGetLocation}
          title="Getting your location..."
        />
      );
    } else if (userLocation) {
      return (
        <GpsFixedIcon
          sx={{
            color: '#4CAF50',
            fontSize: 22,
            cursor: 'pointer',
          }}
          onClick={onGetLocation}
          title={`Location found via ${
            userLocation.method || 'GPS'
          } - Click to refresh`}
        />
      );
    } else {
      return (
        <MyLocationIcon
          sx={{
            color: locationError ? '#F44336' : '#212121',
            fontSize: 22,
            cursor: 'pointer',
          }}
          onClick={onGetLocation}
          title={locationError || 'Click to get your location'}
        />
      );
    }
  };

  const getLocationStatusText = () => {
    if (!userLocation) return null;

    const method = userLocation.method;
    switch (method) {
      case 'GPS':
        return 'Precise GPS';
      case 'IP':
        return `${userLocation.city}, ${userLocation.country}`;
      case 'Timezone':
        return `${userLocation.timezone} region`;
      case 'Manual':
        return 'Manual address';
      default:
        return 'Approximate';
    }
  };

  return (
    <Box>
      {/* Location Status Indicator */}
      {userLocation && !compact && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            backgroundColor: '#E8F5E8',
            borderRadius: 1,
            fontSize: '12px',
            color: '#2E7D32',
          }}
        >
          <Typography variant="caption">
            <span role="img" aria-label="location pin">
              📍
            </span>{' '}
            Location: {getLocationStatusText()}
            {userLocation.method !== 'GPS' && ' (distances are approximate)'}
          </Typography>
        </Box>
      )}

      {/* Location Error */}
      {locationError && !compact && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            backgroundColor: '#FFEBEE',
            borderRadius: 1,
            fontSize: '12px',
            color: '#C62828',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            <span role="img" aria-label="warning">
              ⚠️
            </span>{' '}
            {locationError} - Distances unavailable
          </Typography>
          <Typography
            variant="caption"
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: '#D32F2F' },
            }}
            onClick={handleToggleManualInput}
          >
            <span role="img" aria-label="location pin">
              📍
            </span>{' '}
            Enter your location manually
          </Typography>
        </Box>
      )}

      {/* Compact Location Status */}
      {compact && (userLocation || locationError) && (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {showLocationIcon && (
            <IconButton size="small" onClick={onGetLocation}>
              {getLocationIcon()}
            </IconButton>
          )}
          <Typography
            variant="caption"
            color={locationError ? 'error' : 'text.secondary'}
          >
            {locationError ? (
              <>
                <span role="img" aria-label="warning">
                  ⚠️
                </span>{' '}
                {locationError}
              </>
            ) : (
              <>
                <span role="img" aria-label="location pin">
                  📍
                </span>{' '}
                {getLocationStatusText()}
              </>
            )}
          </Typography>
        </Box>
      )}

      {/* Manual Location Input */}
      {(showManualInputLocal || showManualInput) && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            value={manualAddress}
            onChange={(e) => setManualAddress(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleManualLocation();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    sx={{
                      cursor: 'pointer',
                      color: environment.mainColor,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={handleManualLocation}
                  >
                    SET
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#F8F9FA',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default LocationInput;
